import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { AdminSidebar } from './AdminSidebar';
import { HauserWordmark } from './SidebarShell';

import { useAuthContext } from '@/context/AuthProvider';
import { DashboardLayoutProvider, useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { UploaderProvider } from '@/context/UploaderProvider';

const SIDEBAR_WIDTH = 280;
// the collapsed rail: one width across the app (matches DashboardLayout)
const COLLAPSED_LEFT_WIDTH = 56;

const AdminMobileGate = () => {
  const { logOut } = useAuthContext();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        height: '100dvh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        background: 'transparent',
        padding: '84px 10px 20px',
        fontFamily: "'Manrope', sans-serif",
        boxSizing: 'border-box',
      }}>

      {/* Mobile nav bar — matches DashboardLayout style */}
      <Box
        sx={{
          position: 'fixed',
          top: '12px',
          left: '10px',
          right: '10px',
          height: '60px',
          background: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          backdropFilter: 'blur(10px) saturate(180%)',
          WebkitBackdropFilter: 'blur(10px) saturate(180%)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '16px',
          pl: '20px',
          zIndex: 1202,
        }}>
        <Box
          onClick={() => navigate('/dashboard/summary')}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <HauserWordmark height={22} />
        </Box>
      </Box>

      {/* Gate card */}
      <Box
        sx={{
          background: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          borderRadius: '24px',
          padding: '48px 28px',
          width: '100%',
          maxWidth: 390,
          flex: 1,
          maxHeight: 700,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          boxSizing: 'border-box',
        }}>
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#8b8680"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginBottom: 24 }}>
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
        <Box
          sx={{
            fontSize: 17,
            fontWeight: 300,
            color: 'var(--tx-text)',
            mb: '10px',
            letterSpacing: '-0.02em',
          }}>
          Desktop required
        </Box>
        <Box sx={{ fontSize: 13, color: 'var(--tx-faint)', lineHeight: 1.6, maxWidth: 280 }}>
          The admin dashboard is optimized for desktop. Please switch to a larger screen to continue.
        </Box>
        <Box
          component="button"
          onClick={logOut}
          sx={{
            mt: '32px',
            background: 'var(--tx-fill)',
            border: 0,
            borderRadius: '10px',
            color: 'var(--tx-faint)',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "'Manrope', sans-serif",
            letterSpacing: '-0.01em',
            px: '24px',
            height: '40px',
            cursor: 'pointer',
            transition: 'background-color 0.15s, color 0.15s',
            '&:hover': {
              background: 'var(--tx-fill-2)',
              color: 'var(--tx-text)',
            },
          }}>
          Log out
        </Box>
      </Box>
    </Box>
  );
};

// Tables people scan run the full width of the screen; everything else keeps the 1200px reading cap
const FULL_WIDTH_ROUTES = [
  '/admin/users',
  '/admin/properties',
  '/admin/relationships',
  '/admin/advisor-clients',
  '/admin/inbox',
  '/admin/logic',
  '/admin/infracosts',
  '/admin/kpis',
  '/admin/acquisitions',
];

const AdminDashboardLayoutContent = () => {
  const { isLeftSidebarCollapsed, setLeftSidebarCollapsed } = useDashboardLayout();
  const { pathname } = useLocation();
  const isFullWidth = FULL_WIDTH_ROUTES.some((route) => pathname.startsWith(route));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMidWidth = useMediaQuery('(max-width: 1400px)');

  // Auto-collapse sidebar at mid-width — only on breakpoint change
  const prevMidWidth = useRef(isMidWidth);
  useEffect(() => {
    if (isMidWidth !== prevMidWidth.current) {
      prevMidWidth.current = isMidWidth;
      setLeftSidebarCollapsed(isMidWidth);
    }
  }, [isMidWidth, setLeftSidebarCollapsed]);

  if (isMobile) return <AdminMobileGate />;

  return (
    <Box sx={{ height: '100dvh', display: 'flex' }}>
      {/* Left Sidebar - fixed, overlay at mid-width */}
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          top: '20px',
          bottom: '20px',
          height: 'auto',
          width: isLeftSidebarCollapsed ? COLLAPSED_LEFT_WIDTH : SIDEBAR_WIDTH,
          zIndex: 1200,
          transition: 'width 0.2s ease-in-out',
          borderRadius: '0 25px 25px 0',
          boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
          ...(isMidWidth &&
            !isLeftSidebarCollapsed && {
              boxShadow: '4px 0 24px rgba(0,0,0,0.3)',
            }),
        }}>
        <AdminSidebar />
      </Box>

      {/* Backdrop when sidebar is overlay at mid-width */}
      {isMidWidth && !isLeftSidebarCollapsed && (
        <Box
          onClick={() => setLeftSidebarCollapsed(true)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1199,
            background: 'var(--tx-fill)',
          }}
        />
      )}

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          ml: `${
            (isMidWidth
              ? COLLAPSED_LEFT_WIDTH
              : isLeftSidebarCollapsed
              ? COLLAPSED_LEFT_WIDTH
              : SIDEBAR_WIDTH) + 20
          }px`,
          mr: '20px',
          maxWidth: isFullWidth ? 'none' : 1200,
          transition: 'margin-left 0.2s ease-in-out',
        }}>
        <Box sx={{ flex: 1, overflow: 'auto', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export const AdminDashboardLayout = () => (
  <DashboardLayoutProvider>
    <UploaderProvider>
      <AdminDashboardLayoutContent />
    </UploaderProvider>
  </DashboardLayoutProvider>
);
